import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Divider, Typography, useMediaQuery } from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import jsQR from "jsqr";
import { useZxing } from "react-zxing";
import { extractEvcTokenFromPdfMetadata } from "@syadem/evc-js";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

interface EvcScannerProps {
  onToken: (token: string) => void;
  onError?: (message: string) => void;
  active: boolean;
}

function stripEvcPrefix(token: string): string {
  if (token.startsWith("VC1:")) {
    return token.slice(4);
  }
  return token;
}

export function EvcScanner({ onToken, onError, active }: EvcScannerProps) {
  const { t } = useI18n();
  const [pdfError, setPdfError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const breakpointSm = useMediaQuery(theme.breakpoints.down("sm"));
  const cameraSize = breakpointSm ? 180 : 240;

  const handleToken = (token: string) => {
    setPdfError(null);
    onToken(stripEvcPrefix(token));
  };

  const { ref } = useZxing({
    paused: !active,
    onDecodeResult(result) {
      const text = result.getText();
      if (text.startsWith("http://") || text.startsWith("https://")) return;
      handleToken(text);
    },
  });

  useEffect(() => {
    if (active) return;
    const videoRef = ref as React.MutableRefObject<HTMLVideoElement | null>;
    const video = videoRef?.current;
    if (!video) return;
    const stream = video.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    }
  }, [active, ref]);

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPdfError(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      const { info } = await pdf.getMetadata();
      const pdfMetadata: Record<string, string> = {};
      const infoRecord = info as Record<string, unknown>;
      const customFields = infoRecord?.Custom as Record<string, string> | undefined;
      if (customFields) {
        Object.assign(pdfMetadata, customFields);
      }

      const metadataToken = extractEvcTokenFromPdfMetadata(pdfMetadata);
      if (metadataToken) {
        handleToken(metadataToken);
        return;
      }

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const scale = 2;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          throw new Error("Canvas context not available");
        }

        await page.render({ canvasContext: ctx, viewport, canvas }).promise;
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

        if (qrCode) {
          if (qrCode.data.startsWith("http://") || qrCode.data.startsWith("https://")) {
            continue;
          }
          handleToken(qrCode.data);
          return;
        }
      }

      throw new Error(t("vaccines.scan_evc_no_token"));
    } catch (error) {
      const message = error instanceof Error ? error.message : t("vaccines.scan_evc_pdf_error");
      setPdfError(message);
      if (onError) onError(message);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };


  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          display: { xs: "flex", md: "grid" },
          flexDirection: { xs: "column" },
          gridTemplateColumns: { md: "1fr auto 1fr" },
          gap: 2,
          alignItems: "stretch",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2" align="center">
            {t("vaccines.scan_evc_scan_label")}
          </Typography>
          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${theme.palette.neutral[200]}`,
              background: theme.palette.neutral[100],
              width: cameraSize,
              height: cameraSize,
              maxWidth: "100%",
              alignSelf: "center",
            }}
          >
            <video
              ref={ref as React.RefObject<HTMLVideoElement>}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: "15%",
                border: `2px solid ${theme.palette.warning[500]}`,
                borderRadius: 2,
                backgroundColor: "rgba(255, 215, 56, 0.08)",
                pointerEvents: "none",
              }}
            />
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: "none", md: "block" },
            alignSelf: "stretch",
            borderColor: theme.palette.neutral[200],
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 1,
            border: `1px dashed ${theme.palette.neutral[300]}`,
            borderRadius: 2,
            padding: 2,
          }}
        >
          <Typography variant="subtitle2">{t("vaccines.scan_evc_upload_label")}</Typography>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            id="evc-pdf-upload"
            style={{ display: "none" }}
          />
          <label htmlFor="evc-pdf-upload">
            <Button variant="outlined" component="span">
              {t("vaccines.scan_evc_upload_pdf")}
            </Button>
          </label>
        </Box>
      </Box>

      {pdfError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {pdfError}
        </Alert>
      )}
    </Box>
  );
}
